"use client";

/**
 * Service Status band — expanded network status across 8 islands.
 * Persona-reactive: appends a "your service" line for the active persona's island.
 */
import Link from "next/link";
import { CircleCheck, CloudLightning, MapPin } from "lucide-react";
import { useActivePersona, usePersonaHasHydrated } from "@/lib/persona";
import { useHurricaneSeason, useHurricaneAnchor, useActiveStorm, useNow } from "@petci/shared-ui";
import { getDemoStorm, getOutageSeeds } from "@petci/fixtures";
import { useT, useLocaleTag } from "@/lib/locale";
import { formatEtaLong } from "@/lib/format";

function formatIsland(raw: string): string {
  return raw.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function ServiceStatusBand() {
  const t = useT();
  const tag = useLocaleTag();
  const persona = useActivePersona();
  const hasHydrated = usePersonaHasHydrated();
  const inSeason = useHurricaneSeason(process.env.NEXT_PUBLIC_FORCE_SEASON);
  const hurricaneNow = useHurricaneAnchor(process.env.NEXT_PUBLIC_HURRICANE_ANCHOR_NOW);
  const storm = useActiveStorm(hurricaneNow, getDemoStorm(hurricaneNow), inSeason);
  const now = useNow();
  const outages = getOutageSeeds(now);
  const activeOutages = outages.filter((o) => o.status === "ACTIVE_WITH_ETA" || o.status === "ACTIVE_NO_ETA");
  const isActive = storm.active || activeOutages.length > 0;

  const personaIsland = hasHydrated ? formatIsland(persona.serviceAddress.island) : null;

  if (isActive) {
    const uniqueIslands = new Set(activeOutages.map((o) => o.circuitName.split(" ")[0]));
    return (
      <section id="status" className="bg-white py-6 scroll-mt-20">
        <div className="max-w-6xl 2xl:max-w-[1408px] mx-auto px-4 sm:px-6">
          <div className="bg-status-warning-bg border-l-4 border-l-status-warning rounded-r-lg p-4" role="status">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
              <div className="flex items-center gap-2">
                <CloudLightning size={18} className="text-status-warning flex-shrink-0" aria-hidden />
                <span className="font-semibold text-neutral-900 text-sm">
                  {storm.active
                    ? t("serviceStatus.stormApproaching", { name: storm.name })
                    : t("serviceStatus.activeOutages", { count: String(activeOutages.length) })}
                </span>
              </div>
              <span className="text-sm text-neutral-600">
                {storm.active && `${t("serviceStatus.eta", { eta: formatEtaLong(storm.etaIso, tag) })} · `}
                {activeOutages.length > 0 && t("serviceStatus.outagesAcross", { count: String(activeOutages.length), islandCount: String(uniqueIslands.size) })}
                {personaIsland && ` · ${t("serviceStatus.yourService", { island: personaIsland })}`}
              </span>
              <div className="flex gap-3 sm:ml-auto">
                <Link
                  href="/outage/map"
                  className="text-sm font-medium text-brand-primary-dark hover:underline transition-colors py-1 -my-1"
                >
                  {t("serviceStatus.viewMap")}
                </Link>
                {storm.active && (
                  <Link
                    href="/hurricane"
                    className="text-sm font-medium text-brand-primary-dark hover:underline transition-colors py-1 -my-1"
                  >
                    {t("serviceStatus.hurricanePrep")}
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="status" className="bg-white py-6 scroll-mt-20">
      <div className="max-w-6xl 2xl:max-w-[1408px] mx-auto px-4 sm:px-6">
        <div className="bg-status-success-bg/30 border-l-4 border-l-status-success rounded-r-lg p-4" role="status">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            <div className="flex items-center gap-2">
              <CircleCheck size={18} className="text-status-success flex-shrink-0" aria-hidden />
              <span className="font-semibold text-neutral-900 text-sm">
                {t("serviceStatus.allNormal")}
              </span>
            </div>
            <span className="text-sm text-neutral-500">
              {personaIsland
                ? t("serviceStatus.yourServiceNormal", { island: personaIsland })
                : t("serviceStatus.lastRestoration")
              }
            </span>
            <Link
              href="/outage/map"
              className="text-sm font-medium text-brand-primary-hover hover:text-brand-primary-dark transition-colors sm:ml-auto inline-flex items-center gap-1"
            >
              <MapPin size={14} aria-hidden /> {t("serviceStatus.viewOutageMap")}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
